import React, { useState, useRef, useMemo, useCallback, useEffect } from "react";
import { useSelector } from "react-redux";
import { useHistory } from "react-router-dom";
import { AgGridReact } from "ag-grid-react";
import { FiEye, FiCheck, FiX } from "react-icons/fi";
import { IoMdSearch } from "react-icons/io";
import { TbTableColumn } from "react-icons/tb";
import moment from "moment";
import { toast } from "react-toastify";

import StatusChip, { statusChipType } from "../../../Common/StatusChip";
import ActionButton from "../../../Common/ActionButton";
import { filterStoreProjects } from "../../projectFilters";
import { GetAllGrnApi, UpdateGrnApprovalApi } from "../../../../Api/Operations/PurchaseOrders";
import { useSession } from "../../../../context/SessionContext";

// The logged-in user's pending approval row, only while the GRN awaits approval.
const myPendingApproval = (g, loginId) =>
  g?.Status === "Pending Approval"
    ? g.Approvals?.find((a) => a.ApproverUserId === loginId && a.Status === "Pending")
    : undefined;

/**
 * GRN (Goods Receipt Note) Table Component
 * Route: /store/vendors/GRN
 */
export default function GrnTable() {
  const history = useHistory();
  const rolesData = useSelector((state) => state.GetAllRole);
  const projects = filterStoreProjects(rolesData?.data?.Projects);

  const [selectedProjectId, setSelectedProjectId] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  const [colDropdownOpen, setColDropdownOpen] = useState(false);
  const [columnVisibility, setColumnVisibility] = useState({});
  const [grns, setGrns] = useState([]);
  const [selectedGrnForView, setSelectedGrnForView] = useState(null);
  const { session } = useSession();
  const loginId = session?.LoginId;
  const [reloadKey, setReloadKey] = useState(0);
  const [decision, setDecision] = useState(null); // { approvalId, status, grnNumber }
  const [decisionComments, setDecisionComments] = useState("");
  const [deciding, setDeciding] = useState(false);

  const gridRef = useRef(null);
  const colDropdownRef = useRef(null);

  const openDecision = useCallback(
    (g, status) => {
      const approval = myPendingApproval(g, loginId);
      if (!approval) {
        toast.info("No pending approval found");
        return;
      }
      setDecisionComments("");
      setDecision({ approvalId: approval.Id, status, grnNumber: g.GrnNumber });
    },
    [loginId],
  );

  const confirmDecision = async () => {
    setDeciding(true);
    try {
      const res = await UpdateGrnApprovalApi(
        decision.approvalId,
        decision.status,
        decisionComments,
      );
      if (res?.data?.IsSuccess) {
        toast.success(`GRN ${decision.status.toLowerCase()}`);
        setDecision(null);
        setSelectedGrnForView(null);
        setReloadKey((k) => k + 1);
      } else {
        toast.error(res?.data?.Message || `Failed to update GRN`);
      }
    } catch (err) {
      console.error(err);
      toast.error("Error updating GRN approval");
    } finally {
      setDeciding(false);
    }
  };

  useEffect(() => {
    let cancelled = false;
    GetAllGrnApi(selectedProjectId).then((res) => {
      if (cancelled) {
        return;
      }
      if (res?.data?.IsSuccess) {
        setGrns(res.data.Data || []);
      } else {
        setGrns([]);
        toast.error(res?.data?.Message || "Failed to load GRNs");
      }
    });
    return () => {
      cancelled = true;
    };
  }, [selectedProjectId, reloadKey]);

  const defaultColDef = useMemo(
    () => ({
      sortable: true,
      filter: true,
      resizable: true,
      tooltipValueGetter: (params) => params.value,
      valueFormatter: (params) =>
        params.value === null || params.value === undefined || params.value === ""
          ? "N/A"
          : params.value,
    }),
    [],
  );

  const columnDefs = useMemo(
    () => [
      {
        headerName: "Sr",
        colId: "sr",
        valueGetter: "node.rowIndex + 1",
        width: 60,
        minWidth: 60,
        maxWidth: 70,
        suppressSizeToFit: true,
        filter: false,
        sortable: false,
        cellStyle: { textAlign: "center" },
      },
      {
        headerName: "GRN Number",
        field: "GrnNumber",
        flex: 1.1,
        minWidth: 160,
        cellRenderer: (params) => (
          <span style={{ fontWeight: 600, color: "#0b6462" }}>{params.value || "N/A"}</span>
        ),
      },
      {
        headerName: "PO Number",
        field: "PoNumber",
        flex: 1,
        minWidth: 140,
        cellRenderer: (params) => (
          <span style={{ fontWeight: 500, color: "#2563eb" }}>{params.value || "N/A"}</span>
        ),
      },
      {
        headerName: "Vendor",
        field: "VendorName",
        flex: 1.2,
        minWidth: 160,
      },
      {
        headerName: "Receiving Site / Store",
        field: "ProjectName",
        flex: 1.1,
        minWidth: 150,
      },
      {
        headerName: "GRN Date",
        field: "GrnDate",
        flex: 1,
        minWidth: 130,
        valueFormatter: (params) =>
          params.value ? moment(params.value).format("DD-MMM-YYYY") : "N/A",
      },
      {
        headerName: "Challan / Bilty #",
        field: "ChallanNo",
        flex: 1,
        minWidth: 140,
      },
      {
        headerName: "Vehicle #",
        field: "VehicleNo",
        flex: 0.9,
        minWidth: 120,
      },
      {
        headerName: "Items",
        field: "TotalItems",
        width: 90,
        cellRenderer: (params) => (
          <span
            style={{
              backgroundColor: "#f1f5f9",
              padding: "2px 8px",
              borderRadius: "12px",
              fontSize: "12px",
              fontWeight: 600,
              color: "#475569",
            }}
          >
            {params.value} items
          </span>
        ),
      },
      {
        headerName: "Status",
        field: "Status",
        width: 120,
        cellRenderer: (params) => {
          if (!params.value) {
            return "N/A";
          }
          return <StatusChip status={params.value} type={statusChipType(params.value)} />;
        },
      },
      {
        headerName: "Action",
        colId: "action",
        width: 130,
        filter: false,
        sortable: false,
        headerComponent: () => (
          <div
            style={{
              display: "flex",
              justifyContent: "center",
              width: "100%",
            }}
          >
            <ActionButton
              text="New+"
              newBtn="true"
              onClick={() => history.push("/procurement/GRN/add")}
            />
          </div>
        ),
        cellRenderer: (params) => (
          <div
            style={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              height: "100%",
              gap: "10px",
            }}
          >
            <FiEye
              title="View GRN Details"
              size="1.2em"
              style={{ cursor: "pointer", color: "#0d9488" }}
              onClick={(e) => {
                e.stopPropagation();
                setSelectedGrnForView(params.data);
              }}
            />
            {myPendingApproval(params.data, loginId) && (
              <>
                <FiCheck
                  title="Approve GRN"
                  size="1.2em"
                  style={{ cursor: "pointer", color: "#0e8c6e" }}
                  onClick={(e) => {
                    e.stopPropagation();
                    openDecision(params.data, "Approved");
                  }}
                />
                <FiX
                  title="Reject GRN"
                  size="1.2em"
                  style={{ cursor: "pointer", color: "#c0392b" }}
                  onClick={(e) => {
                    e.stopPropagation();
                    openDecision(params.data, "Rejected");
                  }}
                />
              </>
            )}
          </div>
        ),
      },
    ],
    [history, loginId, openDecision],
  );

  const onGridReady = useCallback((params) => {
    const visibilityMap = {};
    params.api.getColumns()?.forEach((col) => {
      if (col.getColDef().headerName) {
        visibilityMap[col.getColId()] = col.isVisible();
      }
    });
    setColumnVisibility(visibilityMap);
  }, []);

  const toggleColumn = useCallback(
    (colId) => {
      if (!gridRef.current?.api) {
        return;
      }
      const isCurrentlyVisible = columnVisibility[colId];
      gridRef.current.api.setColumnsVisible([colId], !isCurrentlyVisible);
      setColumnVisibility((prev) => ({
        ...prev,
        [colId]: !isCurrentlyVisible,
      }));
    },
    [columnVisibility],
  );

  return (
    <>
      {/* Top Toolbar */}
      <div
        className="d-flex flex-wrap align-items-center justify-content-end pt-3 app_font w-100"
        style={{ gap: "12px", width: "100%" }}
      >
        <div
          className="d-flex align-items-center"
          style={{
            minHeight: "30px",
            maxWidth: "100%",
            marginRight: "auto",
            padding: "4px 12px",
            color: "var(--btn-comp-text, #0b6462)",
            fontSize: "var(--font-md)",
            fontWeight: 600,
            whiteSpace: "nowrap",
          }}
          title="Goods Receipt Note (GRN)"
        >
          Goods Receipt Note (GRN)
        </div>
        {/* Search */}
        <div className="d-flex align-items-center max-w-250" style={{ position: "relative" }}>
          <IoMdSearch
            size="1.1em"
            style={{
              position: "absolute",
              left: 10,
              top: "50%",
              transform: "translateY(-50%)",
              color: "#4e545a",
            }}
          />
          <input
            type="text"
            className="input_styling"
            placeholder="Search GRNs..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            style={{ width: "250px", paddingLeft: 30 }}
            autoComplete="off"
          />
        </div>

        {/* Project Filter */}
        <select
          className="input_styling"
          style={{ marginLeft: "10px", width: "220px" }}
          value={selectedProjectId}
          onChange={(e) => setSelectedProjectId(e.target.value)}
        >
          <option value="">All Projects / Sites</option>
          {projects.map((p) => (
            <option key={p.ProjectId} value={p.ProjectId}>
              {p.ProjectName}
            </option>
          ))}
        </select>

        {/* Column Visibility Dropdown */}
        <div ref={colDropdownRef} style={{ position: "relative", flexGrow: 0 }}>
          <button
            className="input_styling"
            style={{
              display: "flex",
              alignItems: "center",
              gap: "5px",
              cursor: "pointer",
              padding: "4px 10px",
              whiteSpace: "nowrap",
              marginLeft: "10px",
            }}
            onClick={() => setColDropdownOpen((prev) => !prev)}
            title="Show / Hide Columns"
          >
            <TbTableColumn size="1.3em" />
            <span>Columns</span>
          </button>

          {colDropdownOpen && (
            <div
              style={{
                position: "absolute",
                top: "110%",
                right: 0,
                zIndex: 9999,
                background: "#fff",
                border: "1px solid #E8FBFB",
                borderRadius: 6,
                boxShadow: "0 4px 12px rgba(0,0,0,0.12)",
                padding: "8px 0",
                minWidth: 190,
              }}
            >
              {columnDefs.map((col) => (
                <label
                  key={col.colId || col.field}
                  style={{
                    display: "block",
                    padding: "6px 12px",
                    cursor: "pointer",
                    fontSize: "13px",
                  }}
                >
                  <input
                    type="checkbox"
                    checked={columnVisibility[col.colId || col.field] !== false}
                    onChange={() => toggleColumn(col.colId || col.field)}
                    style={{ marginRight: "8px" }}
                  />
                  {col.headerName}
                </label>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Ag-Grid Table */}
      <div
        style={{ width: "100%", height: "750px", marginTop: "18px" }}
        className="ag-theme-alpine"
      >
        <AgGridReact
          ref={gridRef}
          rowData={grns}
          columnDefs={columnDefs}
          defaultColDef={defaultColDef}
          onGridReady={onGridReady}
          pagination={true}
          paginationPageSize={10}
          quickFilterText={searchTerm}
        />
      </div>

      {/* Quick View Modal for GRN */}
      {selectedGrnForView && (
        <div
          className="modalscreen"
          style={{
            display: "block",
            position: "fixed",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: "rgba(0,0,0,0.5)",
            zIndex: 1050,
            overflowY: "auto",
          }}
        >
          <div
            className="modal-content-screen1"
            style={{
              maxWidth: "760px",
              margin: "60px auto",
              background: "#ffffff",
              borderRadius: "14px",
              overflow: "hidden",
              boxShadow: "0 10px 30px rgba(0,0,0,0.2)",
            }}
          >
            {/* Modal Header */}
            <div
              style={{
                background: "linear-gradient(135deg, #dcf6f5 0%, #cdf2f1 100%)",
                padding: "16px 20px",
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                borderBottom: "1px solid #bceae8",
              }}
            >
              <h5 style={{ margin: 0, color: "#164e4d", fontWeight: 700 }}>
                GRN Summary: {selectedGrnForView.GrnNumber}
              </h5>
              <button
                style={{
                  background: "transparent",
                  border: "none",
                  fontSize: "22px",
                  cursor: "pointer",
                  color: "#4b6b6a",
                }}
                onClick={() => setSelectedGrnForView(null)}
              >
                &times;
              </button>
            </div>

            {/* Modal Body */}
            <div style={{ padding: "20px 24px" }}>
              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: "1fr 1fr",
                  gap: "12px 24px",
                  marginBottom: "20px",
                  fontSize: "13.5px",
                }}
              >
                <div>
                  <strong>Purchase Order:</strong> {selectedGrnForView.PoNumber}
                </div>
                <div>
                  <strong>Vendor:</strong> {selectedGrnForView.VendorName}
                </div>
                <div>
                  <strong>Receiving Site:</strong> {selectedGrnForView.ProjectName}
                </div>
                <div>
                  <strong>Challan / Bilty #:</strong> {selectedGrnForView.ChallanNo}
                </div>
                <div>
                  <strong>Vehicle #:</strong> {selectedGrnForView.VehicleNo}
                </div>
                <div>
                  <strong>Received By:</strong> {selectedGrnForView.ReceivedBy}
                </div>
                <div>
                  <strong>Weighbridge Slip:</strong> {selectedGrnForView.WeighbridgeSlipNo || "N/A"}
                </div>
                <div>
                  <strong>Status:</strong>{" "}
                  <StatusChip
                    status={selectedGrnForView.Status}
                    type={statusChipType(selectedGrnForView.Status)}
                  />
                </div>
              </div>

              {/* Items Table */}
              <h6 style={{ fontWeight: 700, color: "#1e293b", marginBottom: "10px" }}>
                Items Received
              </h6>
              <div
                style={{
                  border: "1px solid #e2e8f0",
                  borderRadius: "8px",
                  overflow: "hidden",
                  marginBottom: "16px",
                }}
              >
                <table style={{ width: "100%", fontSize: "13px", borderCollapse: "collapse" }}>
                  <thead style={{ background: "#f1f5f9" }}>
                    <tr>
                      <th style={{ padding: "8px 12px", textAlign: "left" }}>Item</th>
                      <th style={{ padding: "8px 12px", textAlign: "left" }}>UOM</th>
                      <th style={{ padding: "8px 12px", textAlign: "right" }}>Rec.</th>
                      <th style={{ padding: "8px 12px", textAlign: "right" }}>Acc.</th>
                      <th style={{ padding: "8px 12px", textAlign: "right" }}>Rej.</th>
                      <th style={{ padding: "8px 12px", textAlign: "left" }}>Remarks</th>
                    </tr>
                  </thead>
                  <tbody>
                    {selectedGrnForView.Lines?.map((line, i) => (
                      <tr key={i} style={{ borderBottom: "1px solid #f1f5f9" }}>
                        <td style={{ padding: "8px 12px", fontWeight: 600 }}>{line.ItemName}</td>
                        <td style={{ padding: "8px 12px" }}>{line.Uom}</td>
                        <td style={{ padding: "8px 12px", textAlign: "right" }}>
                          {line.QtyReceived}
                        </td>
                        <td
                          style={{
                            padding: "8px 12px",
                            textAlign: "right",
                            color: "#0f766e",
                            fontWeight: 600,
                          }}
                        >
                          {line.QtyAccepted}
                        </td>
                        <td
                          style={{
                            padding: "8px 12px",
                            textAlign: "right",
                            color: line.QtyRejected > 0 ? "#e11d48" : "#64748b",
                          }}
                        >
                          {line.QtyRejected}
                        </td>
                        <td style={{ padding: "8px 12px" }}>{line.Remarks}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Remarks */}
              {selectedGrnForView.GeneralRemarks && (
                <div style={{ fontSize: "13px", color: "#475569" }}>
                  <strong>General Remarks:</strong> {selectedGrnForView.GeneralRemarks}
                </div>
              )}
            </div>

            {/* Modal Footer */}
            <div
              style={{
                padding: "12px 20px",
                background: "#f8fafc",
                borderTop: "1px solid #e2e8f0",
                display: "flex",
                justifyContent: "flex-end",
                gap: "8px",
              }}
            >
              {myPendingApproval(selectedGrnForView, loginId) && (
                <>
                  <button
                    type="button"
                    className="btn btn-success btn-sm"
                    onClick={() => openDecision(selectedGrnForView, "Approved")}
                  >
                    Approve
                  </button>
                  <button
                    type="button"
                    className="btn btn-danger btn-sm"
                    onClick={() => openDecision(selectedGrnForView, "Rejected")}
                  >
                    Reject
                  </button>
                </>
              )}
              <button
                type="button"
                className="btn btn-secondary btn-sm"
                onClick={() => setSelectedGrnForView(null)}
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {decision && (
        <div
          className="modalscreen"
          style={{ display: "block", height: "100vh", overflow: "auto", zIndex: 1100 }}
        >
          <div className="modal-content-screen1">
            <div className="row sticky-topNew">
              <div className="col-12 page_heading">
                <h4 className="text-center mb-0">
                  {decision.status === "Approved" ? "Approve" : "Reject"} GRN {decision.grnNumber}
                </h4>
                <span className="close pr-2" onClick={() => setDecision(null)}>
                  &times;
                </span>
              </div>
            </div>
            <div className="pl-3 pr-3">
              <div className="mt-3 d-flex ">
                <label className="input_label mr-3">Comments (optional)</label>
                <textarea
                  className="input_address"
                  rows={4}
                  value={decisionComments}
                  onChange={(e) => setDecisionComments(e.target.value)}
                />
              </div>
              <div className="row mt-4">
                <div className="col-12 d-flex justify-content-end" style={{ gap: 8 }}>
                  <ActionButton text="Cancel" variant="cancel" onClick={() => setDecision(null)} />
                  <ActionButton
                    text={decision.status === "Approved" ? "Approve" : "Reject"}
                    variant="save"
                    disabled={deciding}
                    onClick={confirmDecision}
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
